import _ from 'lodash';
import FlightCancellationPolicy from '../components/CruiseBooking/PolicyAndFees/FlightCancellationPolicy';
import RentalCarCancellationPolicy from '../components/CruiseBooking/PolicyAndFees/RentalCarCancellationPolicy';
import CruiseInfo from '../components/TripConfiguration/CruiseInfo';
import CustomInventoryInfo from '../components/TripConfiguration/CustomInventoryInfo';
import FlightInfo from '../components/TripConfiguration/FlightInfo';
import FlightSeatsInfo from '../components/TripConfiguration/FlightSeatsInfo';
import HotelInfo from '../components/TripConfiguration/HotelInfo';
import RentalCarInfo from '../components/TripConfiguration/RentalCarInfo';
import {
  BOOKING_FEES_LABEL,
  BookingStatus,
  TravelItineraryKey,
  TravelTypes,
  getInsuranceProviderDisplayName,
} from '../constants';
import computeTotalSeatCost from './computeTotalSeatCost';
import getItineraryPriceBeforeUpdate from './getItineraryPriceBeforeUpdate';
import getPassengerFlightSeats from './getPassengerFlightSeats';
import getHotelRate from './selectors/getHotelRate.ts';
import getRentalCarRate from './selectors/getRentalCarRate.ts';
import TourInfo from '../components/TripConfiguration/TourInfo';
import getHotelAndRoomNames from './getHotelAndRoomNames';
import getTourBookingStatus from './getTourBookingStatus';
import getTourBookingStatusText from './getTourBookingStatusText';

const currency = require('currency.js');

const getTripConfigurationItems = (
  trip,
  cruiseTotals,
  tripConfirmation,
  updatedItineraries,
  passengers,
  isTripSearch,
  agencyPlanningFees,
  agencyPlanningFeesKeptByAgency,
  doShowAgentCommission,
  agencyPlanningFeeLabel,
  totalPriceBreakdown,
  markupsByTripSegmentId,
) => {
  const lineItems = [];
  const isConfirmation = tripConfirmation && tripConfirmation.bookingResponses;
  const travelers = tripConfirmation ? tripConfirmation.travelers : Object.values(passengers || {});

  if (trip.cruiseItinerary) {
    const targetResponses = isConfirmation
      ? tripConfirmation.bookingResponses.filter(
          (item) => item.tripSegmentId === trip.cruiseItinerary.tripSegmentId,
        )
      : undefined;
    lineItems.push({
      title: 'Cruise',
      key: TravelItineraryKey[TravelTypes.cruise],
      mode: TravelTypes.cruise,
      byline: (trip.cruiseItinerary.itineraryDetail || {}).itineraryTitle,
      price: currency(cruiseTotals.totalAmount).value,
      currencyCode: 'USD',
      oldPrice:
        updatedItineraries && updatedItineraries.length
          ? getItineraryPriceBeforeUpdate({
              updatedItineraries,
              tripSegmentId: trip.cruiseItinerary.tripSegmentId,
            })
          : isConfirmation && trip.cruiseItinerary.cost && trip.cruiseItinerary.cost.price,
      bookingResponses: _.map(targetResponses, (r) => ({
        confirmationCode: r && (r.bookingReference || BookingStatus.failed),
        sabreRecordLocator: r && r.sabreRecordLocator,
        status: r && (r.bookingStatus || BookingStatus.failed),
        roomIndex: r.roomIndex,
        cancellationReference:
          r && r.cancellationNumber && r.bookingStatus === BookingStatus.cancelled ? (
            <div>Cancellation #: {r.cancellationNumber}</div>
          ) : null,
        providerConfirmation: (
          <div>
            Cruise Line Reservation No. :{' '}
            {r && r.vendorConfirmationCode ? (
              r.vendorConfirmationCode
            ) : (
              <span className="bg-red-200 px-1">Failed</span>
            )}
          </div>
        ),
      })),
      info: (
        <CruiseInfo
          itinerary={trip.cruiseItinerary}
          passengerCity={trip.passengerCity ? trip.passengerCity.name : undefined}
          tripConfirmation={tripConfirmation}
        />
      ),
      tripSegmentId: trip.cruiseItinerary.tripSegmentId,
      itinerary: trip.cruiseItinerary,
    });
  }

  if (trip.flightItinerary) {
    const stops = trip.flightItinerary.flightSegments[0].stops
      ? `${trip.flightItinerary.flightSegments[0].stops}-stop`
      : 'Nonstop';
    const targetResponse = isConfirmation
      ? tripConfirmation.bookingResponses.find(
          (item) => item.tripSegmentId === trip.flightItinerary.tripSegmentId,
        )
      : undefined;

    const sabreRecordLocator = targetResponse && targetResponse.sabreRecordLocator;
    const orderId = targetResponse && targetResponse.orderId;
    const confirmationCode = targetResponse && targetResponse.bookingReference;
    const passWithFlightNums = _.filter(
      travelers,
      (t) =>
        t.travelerPreferences &&
        t.travelerPreferences.flightTicketNumbers &&
        Object.keys(t.travelerPreferences.flightTicketNumbers).length,
    );
    const ticketNums =
      passWithFlightNums && passWithFlightNums.length
        ? _.map(passWithFlightNums, (t) =>
            _.map(t.travelerPreferences.flightTicketNumbers, (value, key) =>
              value && value.status ? `${key} (${value.status})` : key,
            ).join(', '),
          ).join(', ')
        : undefined;

    let bylineStop = '';
    if (trip.flightItinerary.isOneWay) {
      bylineStop = ', One-way';
    } else if (trip.flightItinerary.isTwoSegmentRoundTrip) {
      bylineStop = ', Roundtrip';
    } else if (trip.flightItinerary.isMultiCity) {
      bylineStop = ', Multi-city';
    }
    bylineStop += `, ${stops}`;
    let byline = '';

    if (trip.flightItinerary.isMultiCity) {
      byline += _.map(
        trip.flightItinerary.flightSegments,
        (fs) => `${fs.originAirportCode}-${fs.destinationAirportCode}`,
      ).join(' / ');
    } else {
      byline += `${trip.flightItinerary.flightSegments[0].originAirportCode}-${trip.flightItinerary.flightSegments[0].destinationAirportCode}`;
    }

    byline += bylineStop;
    let flightItineraryTotalCost =
      trip.flightItinerary.cost && currency(trip.flightItinerary.cost.price || 0).value;
    if (targetResponse && targetResponse.markupAmount > 0) {
      flightItineraryTotalCost += targetResponse.markupAmount;
    } else if (
      markupsByTripSegmentId &&
      markupsByTripSegmentId[trip.flightItinerary.tripSegmentId] > 0
    ) {
      flightItineraryTotalCost += +markupsByTripSegmentId[trip.flightItinerary.tripSegmentId];
    }
    lineItems.push({
      title: 'Flight',
      key: TravelItineraryKey[TravelTypes.flight],
      mode: TravelTypes.flight,
      byline,
      currencyCode: trip.flightItinerary.cost && trip.flightItinerary.cost.currencyCode,
      price: currency(flightItineraryTotalCost || 0).value,
      oldPrice: getItineraryPriceBeforeUpdate({
        updatedItineraries,
        tripSegmentId: trip.flightItinerary.tripSegmentId,
      }),
      cancellationPolicy: <FlightCancellationPolicy flightItinerary={trip.flightItinerary} />,
      sabreRecordLocator,
      orderId,
      ticketNumbers: ticketNums,
      bookingResponse: targetResponse,
      confirmationCode:
        confirmationCode && confirmationCode === sabreRecordLocator
          ? undefined
          : targetResponse && (confirmationCode || BookingStatus.failed),
      status: targetResponse && (targetResponse.bookingStatus || BookingStatus.failed),
      info: (
        <FlightInfo
          itinerary={trip.flightItinerary}
          flightBookingResponse={targetResponse}
          passengers={travelers}
        />
      ),
      tripSegmentId: trip.flightItinerary.tripSegmentId,
      itinerary: trip.flightItinerary,
      isTicketed: targetResponse && targetResponse.isTicketed,
      isPaymentMethodAdded: targetResponse && targetResponse.isPaymentMethodAdded,
      providerConfirmation:
        targetResponse &&
        targetResponse.airlineConfirmationNumbers &&
        (targetResponse.bookingStatus || '').toLowerCase().indexOf('cancel') === -1 ? (
          <div title={targetResponse.airlineConfirmationNumbers}>
            Airline Confirmation #: {targetResponse.airlineConfirmationNumbers}
          </div>
        ) : null,
      lastDateToPurchaseUtc: targetResponse && targetResponse.lastDateToPurchaseUtc,
      autoCancellationDeadlineUtc: targetResponse && targetResponse.autoCancellationDeadlineUtc,
      pnrAAFacts: targetResponse && targetResponse.pnrAAFacts,
    });

    if (!isTripSearch) {
      const passWithEmd = _.filter(
        _.flatMapDeep(travelers, (t) =>
          Object.values((t.travelerPreferences || {}).flightSeatNumbers || {}),
        ),
        (f) => f && f.emdNumber,
      );
      const seats = getPassengerFlightSeats(travelers, trip.flightItinerary);
      if (seats && seats.length) {
        const seatPrice = computeTotalSeatCost(travelers, trip.flightItinerary);
        lineItems.push({
          title: 'Flight Seats',
          key: 'flightSeats',
          price: seatPrice,
          currencyCode: trip.flightItinerary.cost && trip.flightItinerary.cost.currencyCode,
          emdNumbers:
            passWithEmd && passWithEmd.length
              ? _.uniq(
                  _.map(
                    passWithEmd,
                    (e) => e.emdNumber + (e.emdStatus ? ' (' + e.emdStatus + ')' : ''),
                  ),
                ).join(', ')
              : undefined,
          info: <FlightSeatsInfo itinerary={trip.flightItinerary} passengers={travelers} />,
        });
      }
    }
  }

  if (trip && trip.hotelItineraries && Object.keys(trip.hotelItineraries).length > 0) {
    Object.values(trip.hotelItineraries).forEach((itinerary) => {
      const targetResponse = isConfirmation
        ? tripConfirmation.bookingResponses.find(
            (item) => item.tripSegmentId === itinerary.tripSegmentId,
          )
        : undefined;

      const sabreRecordLocator = targetResponse && targetResponse.sabreRecordLocator;
      const confirmationCode = targetResponse && targetResponse.bookingReference;
      const selectedProduct = itinerary.hotel.products.find(
        (item) => item.code === itinerary.selectedRoomCode,
      );

      const status = targetResponse && (targetResponse.bookingStatus || BookingStatus.failed);
      let changeRoomButtonText;
      let statusText;
      let failureReason;
      if (status === BookingStatus.failed && !confirmationCode) {
        statusText = '❌ Not booked (Action Required)';
        changeRoomButtonText = 'Rebook this room';
        failureReason = '(Room was not reserved. Please rebook or choose another option.)';
      }
      lineItems.push({
        title: 'Hotel',
        key: TravelItineraryKey[TravelTypes.hotel],
        mode: TravelTypes.hotel,
        id: itinerary.id,
        // in general probably need to simplify the whole trip data structure
        byline: getHotelAndRoomNames(itinerary),
        currencyCode: selectedProduct.roomPrice.currencyCode,
        price: currency(
          getHotelRate({
            rate: selectedProduct.roomPrice,
            trip,
            itineraryId: itinerary.id,
          }).price,
        ).value,
        oldPrice: getItineraryPriceBeforeUpdate({
          updatedItineraries,
          tripSegmentId: itinerary.tripSegmentId,
        }),
        sabreRecordLocator,
        confirmationCode:
          confirmationCode && confirmationCode === sabreRecordLocator
            ? undefined
            : targetResponse && confirmationCode,
        status,
        statusText,
        failureReason,
        changeRoomButtonText,
        cancellationPolicy: itinerary.selectedProduct.cancellationPolicy,
        bookingResponse: targetResponse,
        info: (
          <HotelInfo
            itinerary={itinerary}
            selectedRoomCode={itinerary.selectedRoomCode}
            tripId={trip.id || trip.tripCode}
            doShowAgentCommission={doShowAgentCommission}
          />
        ),
        tripSegmentId: itinerary.tripSegmentId,
        itinerary,
        providerConfirmation:
          targetResponse && targetResponse.vendorConfirmationCode ? (
            <div>
              Accommodation Confirmation #:{' '}
              {targetResponse.vendorConfirmationCode.replace(/[-]+$/, '')}
            </div>
          ) : null,
        cancellationReference:
          targetResponse &&
          targetResponse.cancellationNumber &&
          targetResponse.bookingStatus === BookingStatus.cancelled ? (
            <div>Cancellation #: {targetResponse.cancellationNumber}</div>
          ) : null,
      });
    });
  }

  if (trip.rentalCarItineraries) {
    Object.values(trip.rentalCarItineraries).forEach((item) => {
      const targetResponse = isConfirmation
        ? tripConfirmation.bookingResponses.find(
            (response) => response.tripSegmentId === item.tripSegmentId,
          )
        : undefined;
      const sabreRecordLocator = targetResponse && targetResponse.sabreRecordLocator;
      const confirmationCode = targetResponse && targetResponse.bookingReference;
      const carRate = getRentalCarRate({ rate: item.cost, trip, itineraryId: item.id });
      lineItems.push({
        title: 'Car',
        key: TravelItineraryKey[TravelTypes.carrental],
        id: item.id,
        mode: TravelTypes.carrental,
        // in general probably need to simplify the whole trip data structure
        byline: `${item.locationInfo.originLocationCode} - ${item.locationInfo.destLocationCode}`,
        price: currency(carRate.price).value,
        currencyCode: carRate.currencyCode,
        oldPrice: getItineraryPriceBeforeUpdate({
          updatedItineraries,
          tripSegmentId: item.tripSegmentId,
        }),
        sabreRecordLocator,
        confirmationCode:
          confirmationCode && confirmationCode === sabreRecordLocator
            ? undefined
            : targetResponse && (confirmationCode || BookingStatus.failed),
        status: targetResponse && (targetResponse.bookingStatus || BookingStatus.failed),
        cancellationPolicy: <RentalCarCancellationPolicy rentalCarItinerary={item} />,
        info: <RentalCarInfo itinerary={item} tripId={trip.id || trip.tripCode} />,
        tripSegmentId: item.tripSegmentId,
        itinerary: item,
        providerConfirmation:
          targetResponse && targetResponse.vendorConfirmationCode ? (
            <div>
              Rental car confirmation #:{' '}
              {targetResponse.vendorConfirmationCode.replace(/[-]+$/, '')}
            </div>
          ) : null,
        cancellationReference:
          targetResponse &&
          targetResponse.cancellationNumber &&
          targetResponse.bookingStatus === BookingStatus.cancelled ? (
            <div>Cancellation #: {targetResponse.cancellationNumber}</div>
          ) : null,
      });
    });
  }

  (trip.tourItineraries || []).forEach((tourItinerary) => {
    const targetResponses = isConfirmation
      ? tripConfirmation.bookingResponses
          .filter((item) => item.tripSegmentId === tourItinerary.tripSegmentId)
          // A tour has one booking per segment, so its status belongs on the line item. Repeating it
          // per booking response is what used to print it twice.
          .map((targetResponse) => ({
            ...targetResponse,
            confirmationCode: targetResponse.vendorConfirmationCode,
            providerNotes: targetResponse.providerNotes,
            freeCancellationUntil: targetResponse.freeCancellationUntil,
          }))
      : undefined;
    lineItems.push({
      title: 'Tour',
      key: TravelItineraryKey[TravelTypes.tour],
      id: tourItinerary.id,
      mode: TravelTypes.tour,
      byline: tourItinerary.tour?.name,
      price: currency(tourItinerary.selectedSlot?.price || 0).value,
      currencyCode:
        tourItinerary.selectedSlot?.currencyCode || tourItinerary.cost?.currencyCode || 'USD',
      info: <TourInfo itinerary={tourItinerary} />,
      itinerary: tourItinerary,
      bookingResponses: targetResponses,
      status:
        targetResponses && targetResponses.length > 0 && getTourBookingStatus(targetResponses[0]),
      statusText:
        targetResponses &&
        targetResponses.length > 0 &&
        getTourBookingStatusText(targetResponses[0]),
      cancellationPolicy: tourItinerary.cancellationPolicy,
      tripSegmentId: tourItinerary.tripSegmentId,
    });
  });

  if (trip.customInventoryItinerary) {
    const targetResponse = isConfirmation
      ? tripConfirmation.bookingResponses.find(
          (item) => item.tripSegmentId === trip.customInventoryItinerary.tripSegmentId,
        )
      : undefined;
    const confirmationCode = targetResponse && targetResponse.bookingReference;
    lineItems.push({
      title: trip.customInventoryItinerary.type,
      key: TravelItineraryKey[TravelTypes.custom],
      mode: TravelTypes.hotel,
      byline: trip.customInventoryItinerary.name,
      price: currency(
        trip.customInventoryItinerary.products.find(
          (item) => item.id === trip.customInventoryItinerary.selectedProductId,
        ).pricing.markupPrice,
      ).value,
      confirmationCode: targetResponse && (confirmationCode || BookingStatus.failed),
      status: targetResponse && (targetResponse.bookingStatus || BookingStatus.failed),
      info: <CustomInventoryInfo itinerary={trip.customInventoryItinerary} />,
      tripSegmentId: trip.customInventoryItinerary.tripSegmentId,
      itinerary: trip.customInventoryItinerary,
      providerConfirmation:
        targetResponse && targetResponse.vendorConfirmationCode ? (
          <div>
            {trip.customInventoryItinerary.type} confirmation #:{' '}
            {targetResponse.vendorConfirmationCode.replace(/[-]+$/, '')}
          </div>
        ) : null,
    });
  }

  if (!isConfirmation && trip.insuranceItinerary?.insuranceInfo?.quote) {
    const { quote, upgrades } = trip.insuranceItinerary.insuranceInfo;
    const price = quote.totalCost + upgrades?.reduce((acc, u) => acc + u.price, 0) ?? 0;

    lineItems.push({
      id: quote.planId,
      title: 'Insurance Premium',
      key: 'insurance',
      tripSegmentId: trip.insuranceItinerary.tripSegmentId,
      byline: `${getInsuranceProviderDisplayName(quote.providerName)}: ${quote.planName} • ${quote.planId}`,
      currencyCode: quote.currency || 'USD',
      price,
      mode: TravelTypes.insurance,
      doHideRemoveBtn: true,
    });
  }

  if (isConfirmation && trip.insuranceItinerary) {
    const insuranceResponses = _.chain(tripConfirmation?.bookingResponses || [])
      .filter((b) => b.mode === TravelTypes.insurance)
      .value();

    insuranceResponses.forEach((insuranceResponse) => {
      const isPurchased =
        insuranceResponse.insuranceInfo.success &&
        insuranceResponse.insuranceInfo.canceledOrderInfo?.success !== true;
      const isCancelled =
        insuranceResponse.insuranceInfo.success &&
        insuranceResponse.insuranceInfo.canceledOrderInfo?.success === true;
      if (!isPurchased && !isCancelled) return;

      let statusText;
      let status;

      if (isPurchased) {
        statusText = '✅ Purchased';
        status = BookingStatus.booked;
      }

      if (isCancelled) {
        statusText = '❌ Cancelled';
        status = BookingStatus.cancelled;
      }

      lineItems.push({
        id: insuranceResponse.insuranceInfo.orderNumber,
        title: 'Insurance Premium',
        key: 'insurance',
        tripSegmentId: insuranceResponse.tripSegmentId,
        confirmationCode: insuranceResponse.insuranceInfo.orderNumber,
        byline: `${insuranceResponse.insuranceInfo.coverageDetail[0].planName} • ${insuranceResponse.insuranceInfo.coverageDetail[0].planId}`,
        currencyCode: insuranceResponse.insuranceInfo.currencyCode || 'USD',
        price: insuranceResponse.insuranceInfo.total,
        status,
        statusText,
        orderNumber: insuranceResponse.insuranceInfo.orderNumber,
        itinerary: insuranceResponse,
        mode: TravelTypes.insurance,
        doHideRemoveBtn: true,
      });
    });
  }

  if (!isTripSearch && agencyPlanningFees > 0) {
    const passWithFlightNums = _.filter(
      travelers,
      (t) =>
        t.travelerPreferences &&
        t.travelerPreferences.serviceFeeTicketNumbers &&
        t.travelerPreferences.serviceFeeTicketNumbers.length,
    );
    const ticketNums =
      passWithFlightNums && passWithFlightNums.length
        ? _.map(passWithFlightNums, (t) =>
            t.travelerPreferences.serviceFeeTicketNumbers.join(', '),
          ).join(', ')
        : undefined;

    lineItems.push({
      title: agencyPlanningFeeLabel || 'Travel agency planning fee',
      key: 'travelAgencyPlanningFee',
      price: agencyPlanningFees,
      referenceNumbers: ticketNums,
      currencyCode: 'USD',
      byline:
        agencyPlanningFeesKeptByAgency > 0 && agencyPlanningFees >= agencyPlanningFeesKeptByAgency
          ? `Fee amount $${currency(agencyPlanningFees).value} airdesk keeps $${currency(agencyPlanningFeesKeptByAgency).value}`
          : undefined,
    });
  }

  const serviceFeeItem =
    totalPriceBreakdown &&
    totalPriceBreakdown.total &&
    Array.isArray(totalPriceBreakdown.total.totalItems) &&
    totalPriceBreakdown.total.totalItems.length > 0
      ? totalPriceBreakdown.total.totalItems.find(
          (item) => item.title && item.title === BOOKING_FEES_LABEL,
        )
      : false;
  if (serviceFeeItem) {
    lineItems.push({
      title: BOOKING_FEES_LABEL,
      key: 'travelAgencyBookingFees',
      price: serviceFeeItem.price,
      currencyCode: serviceFeeItem.currencyCode || 'USD',
      doHideRemoveBtn: true,
    });
  }
  return lineItems;
};

export default getTripConfigurationItems;
